/**
 * Sade Sati — phase timeline view.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest, SadeSatiResponse } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function SadeSatiPage({ onBack }: { onBack: () => void }) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [, setSubmitted] = useState<ChartCreateRequest | null>(null);
  const reportMut = useMutation<SadeSatiResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.sadeSati(req),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setSubmitted(req);
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError && e.status === 422)
      return t('अमान्य इनपुट', 'Invalid input');
    return reportMut.error.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button" onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>
      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          🪐 {t('साढ़ेसाती विश्लेषण', 'Sade Sati Analysis')}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(
            '7.5-वर्षीय शनि-गोचर के तीन चरण; तिथि-आधारित।',
            "7.5-year Saturn-transit's three phases; date-by-date.",
          )}
        </p>
      </header>
      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />
      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('गणना हो रही है…', 'Computing…')}
        </p>
      )}
      {report && <SadeSatiView report={report} lang={lang} t={t} />}
    </div>
  );
}

const _PHASE_STYLE: Record<string, string> = {
  pre: 'bg-stone-900/40 ring-stone-800',
  rising: 'bg-amber-900/20 ring-amber-800/50',
  peak: 'bg-rose-950/30 ring-rose-900/50',
  setting: 'bg-amber-900/20 ring-amber-800/50',
  post: 'bg-emerald-900/20 ring-emerald-800/50',
};

function SadeSatiView({
  report, lang, t,
}: {
  report: SadeSatiResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <article className="space-y-4">
      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <p className="text-xs uppercase tracking-wider text-saffron-300">
          {t('वर्तमान स्थिति', 'Current status')}
        </p>
        <p className="mt-2 text-lg font-semibold text-saffron-100">
          {report.is_currently_active
            ? t('साढ़ेसाती सक्रिय है', 'Sade Sati currently active')
            : t('साढ़ेसाती सक्रिय नहीं', 'Sade Sati not active')}
          {' — '}
          {report.current_phase}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.overall_summary_hi : report.overall_summary_en}
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-base font-semibold text-saffron-200">
          {t('चरण-विवरण', 'Phase Details')}
        </h3>
        {report.phases.map((ph, i) => (
          <div
            key={i}
            className={`rounded-md p-4 ring-1 ${_PHASE_STYLE[ph.phase] ?? 'bg-stone-900/40 ring-stone-800'}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium uppercase tracking-wider text-saffron-300">
                {ph.phase}
              </p>
              <p className="text-xs font-mono text-stone-300">
                {ph.start_date} → {ph.end_date}
              </p>
            </div>
            <p className="mt-1 text-sm text-stone-200">
              {t('शनि', 'Saturn')}: {lang === 'hi' ? ph.saturn_rashi_hi : ph.saturn_rashi_en}
            </p>
            <p className="mt-1 text-sm text-stone-100">
              {lang === 'hi' ? ph.theme_hi : ph.theme_en}
            </p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h3 className="mb-2 text-base font-semibold text-saffron-200">
          {t('उपाय', 'Remedies')}
        </h3>
        <ul className="space-y-1 text-sm text-stone-100">
          {(lang === 'hi' ? report.remedies_hi : report.remedies_en).map(
            (r, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-saffron-400">●</span>
                <span>{r}</span>
              </li>
            ),
          )}
        </ul>
      </section>
      <p className="text-center text-[10px] text-stone-600">
        {report.rules_version}
      </p>
    </article>
  );
}
